import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import { fetchMyReviewAssignments, type ConferenceReviewAssignment } from "./api";
import { formatDateTime } from "./format";
import { PageHeader } from "./shells/PageHeader";

function statusLabel(status: string, t: ReturnType<typeof useLanguage>["t"]) {
  if (status === "completed") return t.reviews.statusCompleted;
  if (status === "assigned") return t.reviews.statusAssigned;
  return status;
}

export function ReviewsPage() {
  const { t, locale } = useLanguage();
  const [rows, setRows] = useState<ConferenceReviewAssignment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchMyReviewAssignments()
      .then(setRows)
      .catch((e) => setError(e instanceof Error ? e.message : t.common.error))
      .finally(() => setLoading(false));
  }, [t.common.error]);

  return (
    <div>
      <PageHeader
        title={t.reviews.title}
        lead={t.reviews.lead}
        backTo="/dashboard"
        backLabel={t.nav.dashboard}
      />
      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      {loading ? (
        <p>{t.common.loading}</p>
      ) : rows.length === 0 ? (
        <p className="dn-muted">{t.reviews.empty}</p>
      ) : (
        <div className="dn-table-wrap">
          <table className="dn-table dn-table-responsive">
            <thead>
              <tr>
                <th>{t.reviews.colTitle}</th>
                <th>{t.reviews.colAuthors}</th>
                <th>{t.reviews.colLanguage}</th>
                <th>{t.reviews.colAssigned}</th>
                <th>{t.reviews.colDue}</th>
                <th>{t.reviews.colStatus}</th>
                <th className="dn-col-actions">{t.admin.actions}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id}>
                  <td data-label={t.reviews.colTitle}>
                    {a.title}
                    {a.keywords ? (
                      <>
                        <br />
                        <span className="dn-muted">{a.keywords}</span>
                      </>
                    ) : null}
                  </td>
                  <td data-label={t.reviews.colAuthors}>{a.authors}</td>
                  <td data-label={t.reviews.colLanguage}>{a.language.toUpperCase()}</td>
                  <td data-label={t.reviews.colAssigned}>{formatDateTime(a.assigned_at, locale)}</td>
                  <td data-label={t.reviews.colDue}>{a.due_at ? formatDateTime(a.due_at, locale) : "—"}</td>
                  <td data-label={t.reviews.colStatus}>{statusLabel(a.status, t)}</td>
                  <td className="dn-col-actions" data-label={t.admin.actions}>
                    <Link className="dn-btn dn-btn-ghost" to={`/reviews/${a.id}`}>
                      {a.status === "completed" ? t.reviews.viewReview : t.reviews.openReview}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
